import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import { ButtonV4 } from 'components';
import { routes } from 'appConstants';
import {
  _t,
  getEventTagFromText,
  openApplicationForm,
} from 'utils';

import iconPlus from 'assets/img/main-v4/icon-plus.svg';
import eventHero from 'assets/img/events-v4/event-hero.png';
import photoSerbia from 'assets/img/buyer-v4/serbia.jpg';
import photoBahrain from 'assets/img/buyer-v4/bahrain.jpg';
import photoPakistan from 'assets/img/buyer-v4/pakistan.jpg';
import photoAzerbaijan from 'assets/img/buyer-v4/azerbaijan.png';
import photoIndonesia from 'assets/img/buyer-v4/indonesia.jpg';
import photoUae from 'assets/img/buyer-v4/uae.jpg';
import photoChina from 'assets/img/buyer-v4/china.png';
import photoUzbekistan from 'assets/img/buyer-v4/uzbekistan.jpg';
import photoSaudi from 'assets/img/buyer-v4/saudi.jpg';
import photoCis from 'assets/img/buyer-v4/cis.jpg';
import photoMipf from 'assets/img/buyer-v4/mipf.png';
import photoDialog from 'assets/img/buyer-v4/dialog.jpg';

import styles from './styles.module.scss';

type Plaque = {
  id: string;
  title: string;
  meta: string;
  image?: string;
};

const PLAQUES: Plaque[] = [
  {
    id: 'cseac',
    title: '14-я выставка полупроводникового оборудования, материалов и основных компонентов CSEAC 2026',
    meta: 'Китай, г. Уси\n31.08.2026 – 02.09.2026',
  },
  {
    id: 'serbia',
    title: 'Международная бизнес-миссия в Сербию',
    meta: 'г. Белград, Сербия\n05.10.2026 – 07.10.2026',
    image: photoSerbia,
  },
  {
    id: 'bahrain',
    title: 'Международная бизнес-миссия в Бахрейн',
    meta: 'г. Манама, Бахрейн\n12.10.2026 – 14.10.2026',
    image: photoBahrain,
  },
  {
    id: 'pakistan',
    title: 'Международная бизнес-миссия в Пакистан',
    meta: 'г. Исламабад, Пакистан\n19.10.2026 – 20.11.2026',
    image: photoPakistan,
  },
  {
    id: 'medinex',
    title: 'Международная бизнес-миссия в Азербайджан в рамках выставки MEDINEX',
    meta: 'г. Баку, Азербайджан\n27.10.2026 – 29.10.2026',
    image: photoAzerbaijan,
  },
  {
    id: 'indonesia',
    title: 'Международная бизнес-миссия в Индонезию',
    meta: 'г. Джакарта, Индонезия\n27.10.2026 – 29.10.2026',
    image: photoIndonesia,
  },
  {
    id: 'adipec',
    title: 'Международная бизнес-миссия в ОАЭ в рамках выставки ADIPEC',
    meta: 'г. Дубай, ОАЭ\n02.11.2026 – 04.11.2026',
    image: photoUae,
  },
  {
    id: 'fhc-shanghai',
    title: 'Международная выставка продуктов питания и напитков FHC SHANGHAI GLOBAL FOOD TRADE SHOW',
    meta: 'г. Шанхай, Китай\n10.11.2026 – 12.11.2026',
    image: photoChina,
  },
  {
    id: 'uzbekistan',
    title: 'Международная бизнес-миссия в Узбекистан в рамках выставки UZAGROEXPO',
    meta: 'г. Ташкент, РУ\n16.11.2026 – 18.11.2026',
    image: photoUzbekistan,
  },
  {
    id: 'saudi',
    title: 'Международная бизнес-миссия в Саудовскую Аравию',
    meta: 'г. Эр-Рияд\n23.11.2026 – 25.11.2026',
    image: photoSaudi,
  },
  {
    id: 'cis-online',
    title: 'Международная бизнес-миссия в страны СНГ',
    meta: 'онлайн\n30.11.2026 – 11.12.2026',
    image: photoCis,
  },
  {
    id: 'mosprom-forum',
    title: 'Московский инвестиционно-промышленный форум',
    meta: 'г. Москва\nдата уточняется\nстрана-партнер: Китай',
    image: photoMipf,
  },
  {
    id: 'brics-dialog',
    title: 'Бизнес-диалог «Москва-БРИКС-ШОС»',
    meta: 'г. Москва\nдата уточняется',
    image: photoDialog,
  },
];

const PAGE_SIZE_FIRST = 7;
const PAGE_SIZE_REST = 8;

const getPageSize = (pageIndex: number) => (
  pageIndex === 0 ? PAGE_SIZE_FIRST : PAGE_SIZE_REST
);

const getPageOffset = (pageIndex: number) => {
  if (pageIndex <= 0) {
    return 0;
  }
  return PAGE_SIZE_FIRST + (pageIndex - 1) * PAGE_SIZE_REST;
};

const getTotalPages = (total: number) => {
  if (total <= PAGE_SIZE_FIRST) {
    return 1;
  }
  return 1 + Math.ceil((total - PAGE_SIZE_FIRST) / PAGE_SIZE_REST);
};

const BuyerEventPlaques = () => {
  const [page, setPage] = useState(0);
  const totalPages = getTotalPages(PLAQUES.length);
  const safePage = Math.min(page, totalPages - 1);
  const pageSize = getPageSize(safePage);
  const pageOffset = getPageOffset(safePage);

  const cards = useMemo(
    () => PLAQUES.slice(pageOffset, pageOffset + pageSize),
    [pageOffset, pageSize],
  );

  return (
    <section className={styles.wrap} aria-label={_t('Байерская программа')}>
      <ul className={styles.grid}>
        {cards.map((item, index) => {
          const featured = safePage === 0 && index === 0;
          const photo = !featured ? item.image : undefined;
          let background: { ['--event-hero']?: string; ['--event-photo']?: string } | undefined;
          if (featured) {
            background = { '--event-hero': `url(${eventHero})` };
          } else if (photo) {
            background = { '--event-photo': `url(${photo})` };
          }
          return (
            <li
              key={item.id}
              className={cx(
                styles.card,
                featured && styles.cardFeatured,
                photo && styles.cardPhoto,
              )}
              style={background as React.CSSProperties | undefined}
            >
              <div className={styles.cardTop}>
                <span className={styles.tag}>{_t(getEventTagFromText(item.title))}</span>
              </div>
              <div className={styles.cardBody}>
                <h3 className={styles.cardTitle}>{_t(item.title)}</h3>
                <p className={styles.cardMeta}>{item.meta}</p>
                <ButtonV4
                  type="button"
                  label="Регистрация"
                  variant="outline"
                  tone={featured || photo ? 'frosted' : 'onLight'}
                  iconSrc={iconPlus}
                  onClick={() => openApplicationForm({
                    type: 'event',
                    id: item.id,
                    title: item.title,
                    path: `${routes.resolutions.service.root}/buyer`,
                  })}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {totalPages > 1 ? (
        <nav className={styles.pager} aria-label={_t('Страницы')}>
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              type="button"
              className={cx(styles.pageBtn, safePage === index && styles.pageBtnActive)}
              onClick={() => {
                setPage(index);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {index + 1}
            </button>
          ))}
        </nav>
      ) : null}
    </section>
  );
};

export default BuyerEventPlaques;
